// ProseLink — what an in-app link inside agent-written markdown points at.
//
// Agent prose (Journal summaries, chat replies) links entities as ordinary
// markdown links to their CLIENT route — the URL is the client's public
// API, so there is no bespoke link syntax:
//
//   [Fotosyntes – quiz](/library/m-quiz)      → a resource
//   [Planera hösten](/c/c-123)                → a conversation
//
// `parseHref` lifts an href into the typed target at the boundary (parse,
// don't validate); anything else (external URLs, other routes, junk) is
// `nothing` and stays plain text. `keepKnownLinks` is the anti-invention
// gate for model output: a link whose target the writer was never shown is
// unwrapped to its plain text, so a stale or made-up id never becomes a
// dead link.
//
// Resource routes accepted on read: `/library/{id}` (canonical), and the
// per-kind deep links `/source/{id}`, `/material/{id}`, `/package/{id}`.
// `toHref` always writes the canonical form.
//
// An Author's chat message is plain text, not markdown; the one structure it
// carries is a MENTION: the same `[title](/library/{id})` link, written by
// the composer at the mention's position (D8). `mentionParts` splits such
// text into plain runs and mentions; `mentionedResourceIds` is the single
// text -> references function, so a message's references never drift from
// its text.
export { keepKnownLinks, linkKey, linksIn, mentionedResourceIds, mentionParts, mentionsAsTitles, mkConversationLink, mkMention, mkResourceLink, parseHref, toHref, };
import { exhaustSwitchCase, Maybe, Result } from "@bilda/utils";
import * as ConversationId from "./conversation/id.js";
import * as ResourceId from "./resource/id.js";
const mkResourceLink = (resourceId) => ({
    type: "resource_link",
    resourceId,
});
const mkConversationLink = (conversationId) => ({ type: "conversation_link", conversationId });
const RESOURCE_ROUTES = new Set([
    "library",
    "source",
    "material",
    "package",
]);
const parseHref = (href) => {
    const match = /^\/([a-z]+)\/([^/?#\s]+)\/?$/.exec(href.trim());
    if (match === null)
        return Maybe.nothing;
    // Ids are safe ids (no escaping needed); the parsers below refuse
    // anything else.
    const [, route = "", id = ""] = match;
    if (RESOURCE_ROUTES.has(route)) {
        const r = ResourceId.to(id);
        return Result.isOk(r)
            ? Maybe.just(mkResourceLink(r.value))
            : Maybe.nothing;
    }
    if (route === "c") {
        const r = ConversationId.to(id);
        return Result.isOk(r)
            ? Maybe.just(mkConversationLink(r.value))
            : Maybe.nothing;
    }
    return Maybe.nothing;
};
const toHref = (link) => {
    switch (link.type) {
        case "resource_link":
            return `/library/${link.resourceId}`;
        case "conversation_link":
            return `/c/${link.conversationId}`;
        default:
            return exhaustSwitchCase(link);
    }
};
// A stable string identity for set membership ("resource:<id>").
const linkKey = (link) => {
    switch (link.type) {
        case "resource_link":
            return `resource:${link.resourceId}`;
        case "conversation_link":
            return `conversation:${link.conversationId}`;
        default:
            return exhaustSwitchCase(link);
    }
};
// `[text](href)`, not preceded by `!` (an image). Nested brackets are not
// supported, which matches what the agent writes.
const LINK = /(?<!!)\[([^\]\n]+)\]\(([^)\s]+)\)/g;
const linksIn = (markdown) => Array.from(markdown.matchAll(LINK), (m) => ({
    text: m[1] ?? "",
    href: m[2] ?? "",
}));
// Unwrap every in-app link whose target is unknown (or not an entity link
// at all) to its plain text; known entity links stay, rewritten to their
// canonical href. External http(s) links stay untouched.
const keepKnownLinks = (markdown, isKnown) => markdown.replace(LINK, (whole, text, href) => {
    if (/^https?:\/\//i.test(href))
        return whole;
    return Maybe.match({
        just: (link) => isKnown(link) ? `[${text}](${toHref(link)})` : text,
        nothing: () => text,
    })(parseHref(href));
});
// A link that is not a resource link stays literal text: the Author's own
// brackets are never markup.
const partOf = (whole, text, href) => Maybe.match({
    just: (link) => link.type === "resource_link"
        ? {
            type: "mention_part",
            title: text,
            resourceId: link.resourceId,
        }
        : { type: "text_part", text: whole },
    nothing: () => ({ type: "text_part", text: whole }),
})(parseHref(href));
const mentionParts = (text) => {
    const { parts, end } = [...text.matchAll(LINK)].reduce((acc, m) => {
        const start = m.index ?? 0;
        const before = start > acc.end
            ? [{ type: "text_part", text: text.slice(acc.end, start) }]
            : [];
        return {
            parts: [
                ...acc.parts,
                ...before,
                partOf(m[0], m[1] ?? "", m[2] ?? ""),
            ],
            end: start + m[0].length,
        };
    }, { parts: [], end: 0 });
    return end < text.length
        ? [...parts, { type: "text_part", text: text.slice(end) }]
        : parts;
};
// The resources a text mentions, first appearance first, each once.
const mentionedResourceIds = (text) => {
    const ids = mentionParts(text).flatMap((p) => p.type === "mention_part" ? [p.resourceId] : []);
    return ids.filter((id, i) => ids.indexOf(id) === i);
};
// The text as read aloud: each mention as its written title (a rail
// excerpt, a tooltip).
const mentionsAsTitles = (text) => mentionParts(text)
    .map((p) => (p.type === "mention_part" ? p.title : p.text))
    .join("");
// The mention token for a resource. Brackets and line breaks in the title
// would break the link, so they become spaces; an empty title falls back to
// the id.
const mkMention = (title, resourceId) => {
    const label = title.replace(/[[\]\s]+/g, " ").trim();
    const href = toHref(mkResourceLink(resourceId));
    return `[${label === "" ? resourceId : label}](${href})`;
};
//# sourceMappingURL=proseLink.js.map